import { Anchor, Button, Card, Container, Group, Stack, Table, Text, ThemeIcon, Title } from '@mantine/core'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LuListChecks } from 'react-icons/lu'
import { AssignmentCalendar } from '@/frontend/components/marina/AssignmentCalendar'
import { DataTable } from '@/frontend/components/marina/DataTable'
import { PillNav } from '@/frontend/components/marina/PillNav'
import { StatusBadge } from '@/frontend/components/marina/StatusBadge'
import { useAssignments } from '@/frontend/hooks/useAssignments'
import { useCanWrite } from '@/frontend/hooks/useAuth'

export const Route = createFileRoute('/dashboard/facility/assignments/')({
  validateSearch: (s: Record<string, unknown>): { view?: 'list' | 'calendar' } => ({
    view: s.view === 'calendar' ? 'calendar' : undefined,
  }),
  component: AssignmentsList,
})

function AssignmentsList() {
  const { t } = useTranslation()
  const navigate = useNavigate({ from: Route.fullPath })
  const canWrite = useCanWrite()
  const { view: rawView } = Route.useSearch()
  const view = rawView ?? 'list'
  const setView = (v: 'list' | 'calendar') => navigate({ search: () => ({ view: v }), replace: true })
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 15

  const CATEGORY_ID: Record<string, string> = {
    CUSTOMER_SUPPORT: t('page.facility.tasks.cat_customer_support'),
    MAINTENANCE: t('page.facility.tasks.cat_maintenance'),
    CLEANING: t('page.facility.tasks.cat_cleaning'),
    ADMIN: t('page.facility.tasks.cat_admin'),
    SECURITY: t('page.facility.tasks.cat_security'),
    OTHER: t('page.facility.tasks.cat_other'),
  }

  const listQuery = useAssignments({ q: query, page, pageSize })
  const items = listQuery.data?.items ?? []
  const total = listQuery.data?.total ?? 0

  const calendarQuery = useAssignments({ page: 1, pageSize: 500 })
  const calendarItems = calendarQuery.data?.items ?? []

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Group gap="sm" align="center">
            <ThemeIcon variant="light" color="laut" size="lg" radius="md">
              <LuListChecks size={18} />
            </ThemeIcon>
            <div>
              <Title order={2} ff="var(--font-display)" fw={600}>
                {t('page.facility.tasks.title')}
              </Title>
              <Text size="xs" c="dimmed">
                {t('page.facility.tasks.subtitle')}
              </Text>
            </div>
          </Group>
          {canWrite && (
            <Button
              color="karang"
              size="sm"
              onClick={() => navigate({ to: '/dashboard/facility/assignments/new' as any })}
            >
              {t('page.facility.tasks.btn_new')}
            </Button>
          )}
        </Group>

        <Group justify="space-between" wrap="wrap" gap="sm">
          <PillNav
            value={view}
            onChange={setView}
            items={[
              {
                value: 'list',
                label: t('page.facility.tasks.tab_list'),
                count: view === 'list' ? total || undefined : undefined,
                countColor: 'gray',
              },
              {
                value: 'calendar',
                label: t('page.facility.tasks.tab_calendar'),
                count: view === 'calendar' ? calendarItems.filter((a) => a.deadline).length || undefined : undefined,
                countColor: 'laut',
              },
            ]}
          />
          {view === 'list' && (
            <input
              type="search"
              value={search}
              placeholder={t('page.facility.tasks.placeholder')}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setQuery(search)
                  setPage(1)
                }
              }}
              style={{
                fontSize: '0.8rem',
                padding: '6px 12px',
                borderRadius: 8,
                border: '1px solid var(--mantine-color-default-border)',
                background: 'var(--app-card-bg)',
                color: 'inherit',
                width: 220,
                outline: 'none',
              }}
            />
          )}
        </Group>

        {view === 'list' ? (
          <Card radius="md" padding="lg">
            <DataTable
              isEmpty={!listQuery.isLoading && items.length === 0}
              emptyLabel={t('page.facility.tasks.empty')}
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={setPage}
              columns={
                <>
                  <Table.Th>{t('page.facility.tasks.col_title')}</Table.Th>
                  <Table.Th>{t('page.facility.tasks.col_assigned')}</Table.Th>
                  <Table.Th>{t('page.facility.tasks.col_priority')}</Table.Th>
                  <Table.Th>{t('page.facility.tasks.col_deadline')}</Table.Th>
                  <Table.Th>{t('page.facility.tasks.col_category')}</Table.Th>
                  <Table.Th>{t('page.facility.tasks.col_status')}</Table.Th>
                  <Table.Th ta="right">{t('common.actions')}</Table.Th>
                </>
              }
            >
              {items.map((a) => (
                <Table.Tr
                  key={a.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate({ to: `/dashboard/facility/assignments/${a.id}` as any })}
                >
                  <Table.Td>
                    <Text size="sm" fw={600}>
                      {a.title}
                    </Text>
                    {a.startDate && (
                      <Text size="xs" c="dimmed">
                        Mulai {dayjs(a.startDate).format('DD MMM YYYY')}
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{a.assignedStaff?.name ?? '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    {a.priority ? <StatusBadge status={a.priority} kind="assignmentPriority" upper /> : '—'}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c={a.deadline && dayjs(a.deadline).isBefore(dayjs()) ? 'red.6' : undefined}>
                      {a.deadline ? dayjs(a.deadline).format('DD MMM YYYY') : '—'}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{a.category ? (CATEGORY_ID[a.category] ?? a.category) : '—'}</Text>
                  </Table.Td>
                  <Table.Td>
                    {a.status ? <StatusBadge status={a.status} kind="assignmentStatus" upper /> : '—'}
                  </Table.Td>
                  <Table.Td ta="right" onClick={(e) => e.stopPropagation()}>
                    <Anchor
                      component={Link}
                      to={`/dashboard/facility/assignments/${a.id}` as any}
                      c="laut"
                      size="sm"
                      fw={500}
                    >
                      {t('common.see_more')}
                    </Anchor>
                  </Table.Td>
                </Table.Tr>
              ))}
            </DataTable>
          </Card>
        ) : (
          <Card radius="md" padding="lg">
            <AssignmentCalendar
              assignments={calendarItems}
              onSelect={(id) => navigate({ to: `/dashboard/facility/assignments/${id}` as any })}
            />
          </Card>
        )}
      </Stack>
    </Container>
  )
}
